import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  ListItemButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import EventIcon from '@mui/icons-material/Event';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';

import useGlobalSearch, { MIN_GLOBAL_SEARCH_LENGTH } from '../../hooks/useGlobalSearch';
import BookingDetailsModal from '../Bookings/BookingDetailsModal/BookingDetailsModal';
import VoucherDetailsModal from '../Vouchers/VoucherDetailsModal';
import ResourceVoucherDetailsModal from '../Vouchers/ResourceVoucherDetailsModal';

/** Suggestions appear this long after the last keystroke. */
const DEBOUNCE_MS = 500;

const BOOKING_STATUS_LABELS = {
  confirmed: 'Bestätigt',
  awaitingConfirmation: 'Bestätigung ausstehend',
  waitingForPayment: 'Wartet auf Zahlung',
  canceled: 'Storniert',
  rejected: 'Abgelehnt',
  noShow: 'No-Show',
  blocked: 'Blocker',
  completed: 'Abgeschlossen',
};

const VOUCHER_STATUS_LABELS = {
  active: 'Aktiv',
  waitingForPayment: 'Unbezahlt',
  used: 'Eingelöst',
  locked: 'Gesperrt',
  expired: 'Abgelaufen',
};

const formatDate = (value, withTime = false) => {
  if (!value) return '';
  const d = dayjs(value);
  if (!d.isValid()) return '';
  return d.format(withTime ? 'DD.MM.YYYY HH:mm' : 'DD.MM.YYYY');
};

const formatEuro = value =>
  typeof value === 'number'
    ? new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value)
    : '';

const bookingTitle = booking => {
  const name = [booking.firstName, booking.lastName].filter(Boolean).join(' ').trim();
  return booking.companyName || name || booking.mail || booking.bookingNumber || 'Buchung';
};

// A cart booking is confirmed under its Warenkorb number, so a search for that
// number has to show which cart the suggestion belongs to.
const bookingSubtitle = booking =>
  [
    booking.bookingNumber,
    booking.groupBookingNumber ? `Warenkorb ${booking.groupBookingNumber}` : null,
    formatDate(booking.startTime, true),
    booking.entertainment,
    booking.mail,
  ]
    .filter(Boolean)
    .join(' · ');

const voucherSubtitle = voucher => {
  const parts = [voucher.kind === 'value' ? 'Wertgutschein' : 'Produktgutschein'];
  if (voucher.kind === 'value' && typeof voucher.currentBalance === 'number') {
    parts.push(`Rest ${formatEuro(voucher.currentBalance)}`);
  }
  if (voucher.kind === 'product' && voucher.productName) parts.push(voucher.productName);
  const expiration = formatDate(voucher.expirationDate);
  if (expiration) parts.push(`gültig bis ${expiration}`);
  if (voucher.buyerMail) parts.push(voucher.buyerMail);
  return parts.filter(Boolean).join(' · ');
};

/**
 * The header search box: one search for the whole app, always over **bookings
 * and vouchers**. Views are never able to take it over any more — a view that
 * filters its own data carries its own search field.
 *
 * Typing shows debounced suggestions grouped into "Buchungen" and "Gutscheine";
 * picking one opens the booking details or the voucher details right where the
 * user is. A complete mail address is answered with every booking and voucher of
 * that customer instead of a short suggestion list.
 */
function HeaderSearch({
  inputRef,
  isSearchExpanded,
  setIsSearchExpanded,
  hasStripedHeaderBackground,
}) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { searchBookingsAndVouchers } = useGlobalSearch();

  const [searchQuery, setSearchQuery] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [bookingId, setBookingId] = useState(null);
  const [voucher, setVoucher] = useState(null);

  const containerRef = useRef(null);
  // Only the newest request may write the result — `useHttp` has no abort support.
  const requestIdRef = useRef(0);
  // Kept in a ref so the debounce below depends on the query alone: a new
  // identity of the search function must not restart the timer.
  const searchRef = useRef(searchBookingsAndVouchers);
  searchRef.current = searchBookingsAndVouchers;

  const trimmedQuery = searchQuery.trim();
  const queryTooShort = trimmedQuery.length < MIN_GLOBAL_SEARCH_LENGTH;
  const showDropdown = open && !queryTooShort;

  const bookings = useMemo(() => result?.bookings?.items || [], [result]);
  const vouchers = useMemo(() => result?.vouchers?.items || [], [result]);
  const hasResults = bookings.length > 0 || vouchers.length > 0;

  // Debounced search; a query below the minimum length clears the list.
  useEffect(() => {
    if (queryTooShort) {
      setResult(null);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const timeout = setTimeout(async () => {
      const requestId = ++requestIdRef.current;
      try {
        const data = await searchRef.current(trimmedQuery);
        if (requestId !== requestIdRef.current) return;
        setResult(data);
      } catch (error) {
        if (requestId !== requestIdRef.current) return;
        setResult(null);
        toast.error('Fehler bei der Suche nach Buchungen und Gutscheinen');
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [trimmedQuery, queryTooShort]);

  // A fresh query invalidates the previous keyboard selection.
  useEffect(() => {
    setActiveIndex(-1);
  }, [trimmedQuery]);

  // Clicking anywhere else closes the suggestions.
  useEffect(() => {
    if (!showDropdown) return undefined;
    const onPointerDown = event => {
      if (!containerRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [showDropdown]);

  const closeDropdown = useCallback(() => {
    setOpen(false);
    setActiveIndex(-1);
  }, []);

  const openBooking = useCallback(
    id => {
      setBookingId(id);
      closeDropdown();
    },
    [closeDropdown]
  );

  const openVoucher = useCallback(
    (id, kind) => {
      setVoucher({ id, kind });
      closeDropdown();
    },
    [closeDropdown]
  );

  // Flat order of the rendered rows, so ↑/↓ can walk both groups as one list.
  const flatItems = useMemo(
    () => [
      ...bookings.map(booking => ({ type: 'booking', booking })),
      ...vouchers.map(v => ({ type: 'voucher', voucher: v })),
    ],
    [bookings, vouchers]
  );

  const goToFullSearch = useCallback(
    path => {
      navigate(path, { state: { initialSearch: trimmedQuery } });
      closeDropdown();
    },
    [navigate, trimmedQuery, closeDropdown]
  );

  // Which full-search view a plain Enter should open: the domain that actually
  // answered the query, bookings first when both did. A voucher code matches no
  // booking at all, so defaulting to the booking search would strand the user on
  // "Keine Buchungen gefunden" while the dropdown was showing the voucher. While
  // a newer query is still loading the result is stale and must not steer the
  // routing, so it falls through to the plain default.
  const getFullSearchTarget = useCallback(() => {
    const bookingsAllowed = result?.bookings?.allowed !== false;
    const vouchersAllowed = result?.vouchers?.allowed !== false;

    if (result && !loading) {
      if (bookingsAllowed && (result.bookings?.total || 0) > 0) return '/bookingSearch';
      if (vouchersAllowed && (result.vouchers?.total || 0) > 0) return '/voucherSearch';
    }
    if (bookingsAllowed) return '/bookingSearch';
    return vouchersAllowed ? '/voucherSearch' : null;
  }, [result, loading]);

  const handleKeyDown = event => {
    if (event.key === 'Escape') {
      closeDropdown();
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (!flatItems.length) return;
      event.preventDefault();
      setOpen(true);
      setActiveIndex(prev => {
        const next = event.key === 'ArrowDown' ? prev + 1 : prev - 1;
        if (next < 0) return flatItems.length - 1;
        if (next >= flatItems.length) return 0;
        return next;
      });
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const item = flatItems[activeIndex];
      if (item) {
        if (item.type === 'booking') openBooking(item.booking._id);
        else openVoucher(item.voucher._id, item.voucher.kind);
        return;
      }
      // Without a highlighted suggestion, Enter opens the full search of the
      // matching domain — the place that can page through more than the
      // suggestion list shows.
      if (queryTooShort) return;
      const target = getFullSearchTarget();
      if (target) goToFullSearch(target);
    }
  };

  const handleClear = event => {
    event.preventDefault();
    event.stopPropagation();
    setSearchQuery('');
    closeDropdown();
  };

  const renderRow = ({ key, icon, title, subtitle, status, index }) => (
    <ListItemButton
      key={key}
      selected={activeIndex === index}
      // The input keeps the focus, so the click must fire before the blur.
      onMouseDown={event => event.preventDefault()}
      onClick={() => {
        const item = flatItems[index];
        if (!item) return;
        if (item.type === 'booking') openBooking(item.booking._id);
        else openVoucher(item.voucher._id, item.voucher.kind);
      }}
      onMouseEnter={() => setActiveIndex(index)}
      sx={{ alignItems: 'center', gap: 1, py: 1 }}
    >
      <Box sx={{ display: 'flex', color: 'text.secondary' }}>{icon}</Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant='body2' fontWeight={600} noWrap>
          {title}
        </Typography>
        <Typography variant='caption' color='text.secondary' noWrap display='block'>
          {subtitle}
        </Typography>
      </Box>
      {status && (
        <Typography variant='caption' color='text.secondary' sx={{ whiteSpace: 'nowrap' }}>
          {status}
        </Typography>
      )}
    </ListItemButton>
  );

  const renderGroupHeader = (label, group, shown) => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        px: 1.5,
        py: 0.75,
        backgroundColor: theme.palette.action.hover,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Typography
        variant='caption'
        fontWeight={700}
        sx={{ textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}
      >
        {label}
      </Typography>
      <Typography variant='caption' color='text.secondary'>
        {group.total > shown ? `${shown} von ${group.total}` : group.total}
      </Typography>
    </Box>
  );

  return (
    <>
      <Box
        ref={containerRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          width: {
            xs: isSearchExpanded ? 'calc(100% - 48px)' : 'auto',
            sm: isSearchExpanded ? 'calc(100% - 48px)' : 'auto',
            md: '100%',
          },
          '@media (max-width: 600px)': {
            width: 'calc(100% - 16px)',
            maxWidth: 'none',
          },
          maxWidth: {
            xs: isSearchExpanded ? 'none' : 120,
            sm: isSearchExpanded ? 'none' : 150,
            md: 400,
            lg: 500,
          },
          transition: 'all 0.3s ease-in-out',
          zIndex: isSearchExpanded ? 1000 : 2,
        }}
      >
        <TextField
          size='small'
          placeholder='Buchungen & Gutscheine (Name, Mail, Nummer, Code)'
          value={searchQuery}
          onChange={e => {
            setSearchQuery(e.target.value);
            setOpen(true);
          }}
          onKeyDown={handleKeyDown}
          inputRef={inputRef}
          onFocus={e => {
            setIsSearchExpanded(true);
            setOpen(true);
            e.target.select();
          }}
          onClick={() => {
            if (!isSearchExpanded) setIsSearchExpanded(true);
          }}
          onBlur={() => {
            if (!searchQuery.trim()) setIsSearchExpanded(false);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon sx={{ color: 'action.active' }} />
              </InputAdornment>
            ),
            endAdornment:
              searchQuery.trim().length > 0 ? (
                <InputAdornment position='end'>
                  {loading && <CircularProgress size={16} sx={{ mr: 0.5 }} />}
                  <Tooltip title='Suche zurücksetzen'>
                    <IconButton
                      type='button'
                      size='small'
                      onMouseDown={e => e.preventDefault()}
                      onClick={handleClear}
                      edge='end'
                      aria-label='Suche zurücksetzen'
                      sx={{
                        color:
                          theme.palette.mode === 'light'
                            ? 'text.secondary'
                            : 'rgba(255, 255, 255, 0.75)',
                        p: 0.5,
                      }}
                    >
                      <ClearIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ) : undefined,
            sx: {
              backgroundColor: hasStripedHeaderBackground
                ? theme.palette.background.paper
                : theme.palette.mode === 'light'
                  ? 'rgba(0, 0, 0, 0.06)'
                  : 'rgba(0, 0, 0, 0.3)',
              boxShadow: hasStripedHeaderBackground ? theme.shadows[3] : 'none',
              borderRadius: 1,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: hasStripedHeaderBackground ? theme.palette.divider : 'transparent',
                },
                '&:hover fieldset': {
                  borderColor: hasStripedHeaderBackground
                    ? theme.palette.text.secondary
                    : theme.palette.mode === 'light'
                      ? 'rgba(0, 0, 0, 0.15)'
                      : 'rgba(255, 255, 255, 0.2)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: hasStripedHeaderBackground
                    ? theme.palette.primary.main
                    : theme.palette.mode === 'light'
                      ? 'rgba(0, 0, 0, 0.28)'
                      : 'rgba(255, 255, 255, 0.4)',
                },
              },
            },
          }}
          sx={{
            width: '100%',
            '& .MuiInputBase-input': {
              color: theme.palette.mode === 'light' ? 'text.primary' : 'rgba(255, 255, 255, 0.9)',
              '&::placeholder': {
                color:
                  theme.palette.mode === 'light' ? 'text.secondary' : 'rgba(255, 255, 255, 0.6)',
                opacity: 1,
              },
            },
            '& .MuiOutlinedInput-root': {
              minHeight: { xs: 40, sm: 'auto' },
              height: 'auto',
            },
          }}
        />

        {showDropdown && (
          <Paper
            elevation={8}
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              mt: 0.5,
              maxHeight: '70vh',
              overflowY: 'auto',
              zIndex: 1300,
            }}
          >
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1.5 }}>
                <CircularProgress size={16} />
                <Typography variant='body2' color='text.secondary'>
                  Suche läuft …
                </Typography>
              </Box>
            ) : !hasResults ? (
              <Box sx={{ px: 1.5, py: 1.5 }}>
                <Typography variant='body2' color='text.secondary'>
                  Keine Buchungen oder Gutscheine gefunden.
                </Typography>
              </Box>
            ) : (
              <>
                {result?.bookings?.allowed && bookings.length > 0 && (
                  <Box component='section'>
                    {renderGroupHeader('Buchungen', result.bookings, bookings.length)}
                    {bookings.map((booking, index) =>
                      renderRow({
                        key: booking._id,
                        icon: <EventIcon fontSize='small' />,
                        title: bookingTitle(booking),
                        subtitle: bookingSubtitle(booking),
                        status: BOOKING_STATUS_LABELS[booking.status] || booking.status,
                        index,
                      })
                    )}
                    {result.bookings.total > bookings.length && (
                      <ListItemButton
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => goToFullSearch('/bookingSearch')}
                        sx={{ borderTop: `1px solid ${theme.palette.divider}` }}
                      >
                        <Typography variant='caption' color='primary'>
                          Alle {result.bookings.total} Buchungen in der Buchungssuche öffnen
                        </Typography>
                      </ListItemButton>
                    )}
                  </Box>
                )}

                {/* The two groups are separated by their own header band, so a
                    voucher hit is never mistaken for a booking. */}
                {result?.vouchers?.allowed && vouchers.length > 0 && (
                  <Box
                    component='section'
                    sx={
                      bookings.length > 0
                        ? { borderTop: `4px solid ${theme.palette.divider}` }
                        : undefined
                    }
                  >
                    {renderGroupHeader('Gutscheine', result.vouchers, vouchers.length)}
                    {vouchers.map((v, index) =>
                      renderRow({
                        key: `${v.kind}-${v._id}`,
                        icon: <ConfirmationNumberIcon fontSize='small' />,
                        title: v.voucherCode,
                        subtitle: voucherSubtitle(v),
                        status: VOUCHER_STATUS_LABELS[v.status] || v.status,
                        index: bookings.length + index,
                      })
                    )}
                    {result.vouchers.total > vouchers.length && (
                      <ListItemButton
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => goToFullSearch('/voucherSearch')}
                        sx={{ borderTop: `1px solid ${theme.palette.divider}` }}
                      >
                        <Typography variant='caption' color='primary'>
                          Alle {result.vouchers.total} Gutscheine in der Gutscheinverwaltung öffnen
                        </Typography>
                      </ListItemButton>
                    )}
                  </Box>
                )}
              </>
            )}
          </Paper>
        )}
      </Box>

      <BookingDetailsModal
        open={!!bookingId}
        onClose={() => setBookingId(null)}
        bookingId={bookingId}
        onUpdate={() => {}}
      />
      <VoucherDetailsModal
        open={voucher?.kind === 'value'}
        onClose={() => setVoucher(null)}
        voucherId={voucher?.kind === 'value' ? voucher.id : null}
      />
      <ResourceVoucherDetailsModal
        open={voucher?.kind === 'product'}
        onClose={() => setVoucher(null)}
        voucherId={voucher?.kind === 'product' ? voucher.id : null}
      />
    </>
  );
}

export default HeaderSearch;
